/**
 * Hook de gestion du thème (clair / sombre) pour l'Espace Formateur.
 *
 * Stockage : localStorage 'formateur-theme' = 'light' | 'dark'
 * Par défaut : 'light' (mode clair attractif inspiré charte 2i).
 *
 * Applique la classe `dark` sur <html> pour activer les variantes Tailwind
 * `dark:`. Reset au démontage du composant qui n'utilise pas l'Espace formateur
 * (le portail étudiant a sa propre logique).
 */
import { useEffect, useState, useCallback } from 'react';

type Theme = 'light' | 'dark';

const STORAGE_KEY = 'formateur-theme';

function getInitial(): Theme {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'light' || v === 'dark') return v;
  } catch { /* ignore */ }
  // Par défaut clair (la charte 2i est faite pour fond clair)
  return 'light';
}

function applyTheme(t: Theme) {
  const html = document.documentElement;
  if (t === 'dark') html.classList.add('dark');
  else html.classList.remove('dark');
}

export function useFormateurTheme() {
  const [theme, setTheme] = useState<Theme>(getInitial);

  // Applique le thème au mount + à chaque changement
  useEffect(() => {
    applyTheme(theme);
    try { localStorage.setItem(STORAGE_KEY, theme); } catch { /* ignore */ }
  }, [theme]);

  const toggle = useCallback(() => {
    setTheme(t => (t === 'light' ? 'dark' : 'light'));
  }, []);

  return { theme, toggle, isLight: theme === 'light' };
}
